import type { MutableRefObject } from 'react';
import { useComponentHealthProvider } from '../ComponentHealthProvider';
import type { ComponentHealthState, ComponentInfo } from '../types';
import { ACTION_COMPONENT_LOG } from '../constants';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';

/**
 * Custom hook for logging component-level logs. e.g. slow load log
 *
 * @param {MutableRefObject<ComponentInfo>} componentInfoRef - Ref containing component metadata (name, loadId, etc.).
 * @param {ComponentHealthState} componentHealthState - Current health state of the component.
 * @returns {{
 *   onComponentLog: (reason: unknown, context?: object) => void
 * }} - A function to trigger a log event for the component.
 *
 */
export const useComponentLog = (componentInfoRef: MutableRefObject<ComponentInfo>, componentHealthState: ComponentHealthState) => {
  const { onAction } = useComponentHealthProvider();

  const onComponentLog = useEventCallback((reason: unknown, context?: object) => {
    const { componentName, loadId } = componentInfoRef.current;
    onAction(ACTION_COMPONENT_LOG, {
      componentName,
      loadId,
      type: componentHealthState.type,
      reason,
      context,
    });
  });

  return { onComponentLog };
};
